import type {
  IComparisonFilter,
  IFilter,
  IRangeFilter,
  ITextFilter,
  ListingsAPIAndFilter,
  ListingsAPIOrFilter,
} from '../types/ListingsAPIFilter';

class BaseFilterBuilder<T extends { negated: boolean }> {
  protected readonly data: T;

  constructor(data: T) {
    this.data = data;
  }

  negated(): this {
    this.data.negated = true;
    return this;
  }
}

export class ListingsRequestRangeFilterBuilder extends BaseFilterBuilder<IRangeFilter> {
  constructor(key: string) {
    super({
      key,
      negated: false,
    });
  }

  min(n: number, exclusive: boolean = false): this {
    if (this.data.minimum !== undefined) {
      throw new Error('Minimum value has already been set');
    }
    this.data.minimum = n;
    this.data.exclusiveMinimum = exclusive;
    return this;
  }

  max(n: number, exclusive: boolean = false): this {
    if (this.data.maximum !== undefined) {
      throw new Error('Maximum value has already been set');
    }
    this.data.maximum = n;
    this.data.exclusiveMaximum = exclusive;
    return this;
  }

  build(): IRangeFilter {
    if (this.data.minimum === undefined && this.data.maximum === undefined) {
      throw new Error('At least one of minimum or maximum must be set');
    }
    if (
      this.data.minimum !== undefined &&
      this.data.maximum !== undefined &&
      this.data.minimum > this.data.maximum
    ) {
      throw new Error('Minimum cannot be greater than maximum');
    }

    return { ...this.data };
  }
}

export class ListingsRequestComparisonFilterBuilder extends BaseFilterBuilder<IComparisonFilter> {
  constructor(key: string, compareTo: number, comparisonMode: '<=' | '<' | '>=' | '>' | '=') {
    super({
      key,
      compareTo,
      comparisonMode,
      negated: false,
    });
  }

  build(): IComparisonFilter {
    return { ...this.data };
  }
}

export class ListingsRequestTextFilterBuilder extends BaseFilterBuilder<ITextFilter> {
  constructor(key: string, text: string[]) {
    super({
      key,
      text,
      negated: false,
    });
  }

  build(): ITextFilter {
    return { ...this.data };
  }
}

type Filter =
  | ListingsAPIAndFilter
  | ListingsAPIOrFilter
  | ITextFilter
  | IRangeFilter
  | IComparisonFilter;

export class ListingsRequestAndFilterBuilder {
  private readonly data: { and: IFilter[] } = { and: [] };

  filters(filters: Filter[]): this {
    if (this.data.and.length) {
      throw new Error('filters have already been added to the AND filter');
    }
    this.data.and = filters;
    return this;
  }

  build(): ListingsAPIAndFilter {
    if (this.data.and.length === 0) {
      throw new Error('At least one filter must be added to the AND filter');
    }
    return { ...this.data };
  }
}

export class ListingsRequestOrFilterBuilder {
  private readonly data: { or: IFilter[] } = { or: [] };

  filters(filters: Filter[]): this {
    if (this.data.or.length) {
      throw new Error('filters have already been added to the OR filter');
    }
    this.data.or = filters;
    return this;
  }

  build(): ListingsAPIOrFilter {
    if (this.data.or.length === 0) {
      throw new Error('At least one filter must be added to the OR filter');
    }
    return { ...this.data };
  }
}
